import { pxToEm } from 'util/pxToEm';
import { formatRelativeTime } from 'util/formatRelativeTime';
import { NOTIFICATION_TYPES } from 'constants/notification-types';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import React, { ReactElement, useMemo } from 'react';
import { NotificationDto } from 'services/api/apis/notifications';
import { ArrowForwardIcon } from 'components/icons/ArrowForwardIcon';
import { Image } from 'components/lib';

export interface NotificationItemProps {
  notification: NotificationDto;
  onClick: (notification: NotificationDto) => void;
}

// CTA text mapping for notification types
const CTA_MAP: Record<string, string> = {
  // Attendee notifications - QR code
  [NOTIFICATION_TYPES.EVENT_RSVP_CONFIRMATION]: 'Show my check in QR code',
  [NOTIFICATION_TYPES.EVENT_STARTING_SOON_ATTENDEE]: 'Show my check in QR code',
  [NOTIFICATION_TYPES.EVENT_FEEDBACK_REQUEST]: 'Share your thoughts',
  // View event
  [NOTIFICATION_TYPES.EVENT_APPROVED_LEADER]: 'View event',
  [NOTIFICATION_TYPES.EVENT_APPROVED_USER]: 'View event',
  [NOTIFICATION_TYPES.EVENT_TIME_LOCATION_UPDATED]: 'View event',
  [NOTIFICATION_TYPES.CRON_EVENT_START_IN_ONE_HOUR]: 'View event',
  [NOTIFICATION_TYPES.CRON_EVENT_TIME_TO_CHECK_IN]: 'View event',
  // View event status
  [NOTIFICATION_TYPES.EVENT_DENIED_LEADER]: 'View event status',
  [NOTIFICATION_TYPES.EVENT_REJECTED]: 'View event status',
  [NOTIFICATION_TYPES.EVENT_EXPIRED_LEADER]: 'View event status',
  // Check status/in attendees
  [NOTIFICATION_TYPES.EVENT_SUBMISSION]: 'Check status',
  [NOTIFICATION_TYPES.EVENT_STARTING_SOON_LEADER]: 'Check in attendees',
  [NOTIFICATION_TYPES.EVENT_STARTING_SOON_ADMIN]: 'Check in attendees',
  // Approver actions
  [NOTIFICATION_TYPES.EVENT_PENDING_APPROVAL_APPROVER]: 'Click here to take action.',
  [NOTIFICATION_TYPES.EVENT_PENDING_APPROVAL]: 'Click here to take action.',
  // Review requests
  [NOTIFICATION_TYPES.ORG_LEADER_REQUEST]: 'Review request',
  [NOTIFICATION_TYPES.ORG_LEADER_REQUEST_EXEC]: 'Review request',
  [NOTIFICATION_TYPES.ADMIN_REQUEST]: 'Review request',
  [NOTIFICATION_TYPES.ORG_MEMBERSHIP_SETTING_CHANGED]: 'View organization',
  // Cron notifications
  [NOTIFICATION_TYPES.CRON_WEEKLY_UPCOMING_EVENTS]: 'Explore events',
  [NOTIFICATION_TYPES.CRON_DAILY_PENDING_EVENTS]: 'Review events',
};

const getNotificationCTA = (type: string): string | null => CTA_MAP[type] ?? null;

const CANCELLED_NOTIFICATION_TYPES: string[] = [
  NOTIFICATION_TYPES.EVENT_CANCELLED_ATTENDEE,
  NOTIFICATION_TYPES.EVENT_CANCELLED_LEADER,
  NOTIFICATION_TYPES.EVENT_CANCELLED_APPROVER,
  NOTIFICATION_TYPES.EVENT_CANCELLED_ADMIN,
];

const isCancelledType = (type: string): boolean =>
  CANCELLED_NOTIFICATION_TYPES.includes(type);

// Static styles extracted to reduce complexity
const styles = {
  unreadDot: {
    position: 'absolute',
    left: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#9B0000',
  } as const,
  imageContainer: {
    width: 60,
    height: 80,
    minWidth: 60,
    overflow: 'hidden',
    backgroundColor: '#f0f0f0',
    flexShrink: 0,
  } as const,
  imageStyle: {
    width: 60,
    height: 80,
    objectFit: 'cover' as const,
  },
  title: {
    fontSize: pxToEm(14),
    fontWeight: 500,
    color: '#383838',
  } as const,
  timestampContainer: {
    flexShrink: 0,
    marginLeft: 'auto',
  } as const,
  timestamp: {
    fontSize: pxToEm(12),
    fontWeight: 400,
    color: (theme: Theme) => theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  } as SxProps<Theme>,
  bodyText: {
    fontSize: pxToEm(14),
    fontWeight: 400,
    color: '#383838',
    width: '100%',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
  } as const,
  ctaContainer: {
    cursor: 'pointer',
    marginTop: '4px',
    '&:hover': { opacity: 0.8 },
  } as const,
  ctaText: {
    fontSize: pxToEm(12),
    fontWeight: 500,
    color: '#383838',
    textDecoration: 'underline',
  } as const,
  ctaIcon: {
    fontSize: pxToEm(12),
    color: '#383838',
  } as const,
};

const getContainerSx = (isRead: boolean, isCancelled: boolean): SxProps<Theme> => {
  const baseHoverBg = isRead ? 'rgba(0,0,0,0.02)' : '#E8F4FF';
  const cancelledHoverBg = isRead ? 'transparent' : '#F2F9FF';

  return {
    padding: '16px 24px',
    cursor: isCancelled ? 'default' : 'pointer',
    backgroundColor: isRead ? 'transparent' : '#F2F9FF',
    display: 'flex',
    gap: 16,
    alignItems: 'flex-start',
    position: 'relative',
    '&:hover': {
      backgroundColor: isCancelled ? cancelledHoverBg : baseHoverBg,
    },
  };
};

export const NotificationItem = ({
  notification,
  onClick,
}: NotificationItemProps): ReactElement => {
  const eventImageUrl =
    notification.eventImageUrl || notification.data?.eventImageUrl;
  const organizationImageUrl =
    notification.organizationImageUrl || notification.data?.organizationImageUrl;
  const imageUrl = eventImageUrl || organizationImageUrl;
  const eventName =
    notification.eventName || notification.data?.eventName || 'Event';
  const imageAlt = eventName || 'Organization';
  const ctaText = getNotificationCTA(notification.type);
  const isCancelled = !ctaText && isCancelledType(notification.type);

  const containerSx = useMemo(
    () => getContainerSx(notification.isRead, isCancelled),
    [notification.isRead, isCancelled],
  );

  const handleClick = () => {
    if (!isCancelled) onClick(notification);
  };

  const handleCtaClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick(notification);
  };

  return (
    <Box sx={containerSx} onClick={handleClick}>
      <UnreadIndicator isRead={notification.isRead} />
      <EventThumbnail imageUrl={imageUrl} alt={imageAlt} />
      <NotificationContent
        notification={notification}
        ctaText={ctaText}
        onCtaClick={handleCtaClick}
      />
    </Box>
  );
};

// Sub-components to reduce main component complexity
const UnreadIndicator = ({ isRead }: { isRead: boolean }) => {
  if (isRead) return null;
  return <Box sx={styles.unreadDot} />;
};

const EventThumbnail = ({ imageUrl, alt }: { imageUrl?: string; alt: string }) => {
  if (!imageUrl) return null;
  return (
    <Box sx={styles.imageContainer}>
      <Image src={imageUrl} alt={alt} style={styles.imageStyle} />
    </Box>
  );
};

const NotificationContent = ({
  notification,
  ctaText,
  onCtaClick,
}: {
  notification: NotificationDto;
  ctaText: string | null;
  onCtaClick: (e: React.MouseEvent) => void;
}) => (
  <Box flex={1} display="flex" flexDirection="column" gap={10} minWidth={0}>
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Typography sx={styles.title}>{notification.title}</Typography>
      <Box sx={styles.timestampContainer}>
        <Typography sx={styles.timestamp}>
          {formatRelativeTime(new Date(notification.createdAt))}
        </Typography>
      </Box>
    </Box>
    <Box sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
      {(notification.body || notification.eventName) && (
        <Typography component="div" sx={styles.bodyText}>
          {notification.body || notification.eventName}
        </Typography>
      )}
      {ctaText && (
        <Box display="flex" alignItems="center" gap={4} sx={styles.ctaContainer} onClick={onCtaClick}>
          <Typography sx={styles.ctaText}>{ctaText}</Typography>
          <ArrowForwardIcon sx={styles.ctaIcon} />
        </Box>
      )}
    </Box>
  </Box>
);

